import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  type ReactElement,
} from "react";
import { Button } from "antd";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useMenuItems, useStep } from "./constants";
import {
  getItems,
  getCancelButtonLabel,
  getRatesTrialStepState,
  getEffectiveCheckValueForStep,
  getPrimaryNextLabel,
} from "./utils";
import useFormHandling from "./hooks/useFormHandling";
import { useTrial } from "./hooks/useTrial";
import TrialForm from "./TrialsForm";
import BackConfirmationModal from "./BackConfirmationModal";
import TrialHeader from "./TrialHeader";
import SummaryFooter from "./SummaryFooter";
import { useParams, useLocation } from "react-router-dom";
import AxiosInstance from "../../shared/utils/axios";
import dayjs from "dayjs";
import { useNewTrialState } from "./hooks/NewTrialStateProvider";
import TrialMapComponent from "./trialMapComponent";
import "../style.less";
import type { monitorModaldata } from "../../shared/layout/types";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../shared/utils/translationUtils";
import { Loader } from "../../shared/components/Loader";
import useDownloadWorkOrder from "../../shared/custom-hooks/useDownloadWorkOrder";
import { ReactComponent as ArrowOutBtn } from "../../assets/images/sidebar_close.svg";
import { ReactComponent as ArrowInBtn } from "../../assets/images/sidebar_open.svg";
import { useTrialsRouter } from "../../shared/custom-hooks/useTrialsRouter";
import { useFeatureFlags } from "../../context/LDFlagsContext";
import { useTrackingEvent } from "../../shared/custom-hooks/useTracking";
import { TrialAmplitudeEvent } from "../../shared/utils/amplitudeEvents";

const NewTrial = (): ReactElement => {
  const didSetStep = useRef(false);
  const { t } = useTranslation();
  const [openBackModal, setOpenBackModal] = useState(false);
  const menuItems = useMenuItems();

  const {
    trialInfo,
    setTrialInfo,
    newTrialState,
    trialProtocolData,
    setTrialProtocolData,
    isFertilizerTabActive,
    setIsFertilizerTabActive,
    activeRatesTab,
    setActiveRatesTab,
    ratesDosageValidationByType,
    resetStates,
    setRatesDosage,
  } = useNewTrialState();

  const isStripe = !!newTrialState?.stripsData?.isStripMode;
  const { router, workspaceId, orgId } = useTrialsRouter();
  const steps = useStep(isStripe);

  const showBackModal = (): void => {
    setOpenBackModal(true);
  };
  const handleOkBackModal = (): void => {
    router.goToTrialsList();
  };

  const handleCancelModal = (): void => {
    setOpenBackModal(false);
  };

  const extraHeaders = {
    "org-id": orgId ?? "",
    "workspace-id": workspaceId ?? "",
  };

  const {
    error,
    form,
    current,
    checkValue,
    prev,
    onFinish,
    setCurrent,
    saveRatesDosageSubStep,
  } = useFormHandling();

  const items = getItems(steps);
  const location = useLocation();
  const { id } = useParams();
  const trialId = id ?? trialInfo?.id;
  const {
    data: trialData,
    isLoading: isLoadingTrialData,
    refetch: refetchTrial,
  } = useTrial(trialId);
  const locationState = location.state as { goToSummary?: boolean } | null;
  const isCompletedTrial: boolean =
    trialInfo?.completion === true || trialData?.completion === true;
  const goToSummary: boolean =
    isCompletedTrial || locationState?.goToSummary === true;

  const ratesTrialStepState = getRatesTrialStepState(
    trialProtocolData?.protocol_type,
    activeRatesTab,
    ratesDosageValidationByType
  );
  const { nextRatesTrialType } = ratesTrialStepState;

  const effectiveCheckValueForStep = getEffectiveCheckValueForStep(
    current,
    checkValue,
    ratesTrialStepState
  );

  const primaryNextLabel = getPrimaryNextLabel(
    current,
    nextRatesTrialType,
    t,
    formatTranslation,
    steps
  );

  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  const [open, setOpen] = useState(false);
  // Controls the AgOperationDataLoader modal shown while the latest trial data
  // is refetched before opening the export flow.
  const [agLoaderOpen, setAgLoaderOpen] = useState(false);
  const [agOperationIds, setAgOperationids] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const { downloadISOXMLOrder, downloadmonitorModalForm, isLoading } =
    useDownloadWorkOrder();
  const trackTrialsEvents = useTrackingEvent({
    page: TrialAmplitudeEvent.TRIALS_PAGE,
    group: TrialAmplitudeEvent.TRIALS_GROUP,
    module: TrialAmplitudeEvent.TRIALS_GROUP,
  });
  const [drawerVisible, setDrawerVisible] = useState(false);
  // local fix: reportDrawerKey forces a full remount of the drawer on each open,
  // ensuring stale report state is cleared between invocations.
  const [reportDrawerKey, setReportDrawerKey] = useState(0);
  const [isLoadingTrialPlotData, setIsLoadingTrialPlotData] = useState(false);

  const { enableNewWorkorderExport } = useFeatureFlags();

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  useEffect(() => {
    if ((trialId && current === 2) || current === 1 || current === 0) {
      void trackTrialsEvents({
        event: TrialAmplitudeEvent.TRIAL_DESIGN,
        originAction: id ? "Edit Trial" : "New Trial",
      });
    }
  }, [id, current, trialId]);

  useEffect(() => {
    if (trialData) {
      form.setFieldsValue({
        name: trialData?.name,
        protocol_id: trialData?.protocol_id,
        field_ids: trialData?.field_ids,
        rangePicker: [dayjs(trialData?.start_date), dayjs(trialData?.end_date)],
      });
      setTrialInfo(trialData);
      if (trialData?.standard_field_properties) {
        const sfp = trialData.standard_field_properties;
        setRatesDosage((prev) => {
          const hasLocalRatesData =
            (prev?.standard_seed_rate ?? 0) > 0 ||
            Boolean(prev?.standard_product?.trim?.()) ||
            (prev?.standard_fertilisers?.length ?? 0) > 0 ||
            (prev?.standard_biological_application_settings?.length ?? 0) > 0 ||
            (prev?.standard_tank_mix_rates?.length ?? 0) > 0 ||
            prev?.rates_and_dosages?.some((plotRates: any[]) =>
              plotRates?.some((rateItem: any) => {
                const hasSeedValues =
                  (rateItem?.rate ?? 0) > 0 ||
                  Boolean(rateItem?.variety?.trim?.()) ||
                  Boolean(rateItem?.variety_name?.trim?.());
                const hasFertiliserValues =
                  (rateItem?.fertiliser_rate ?? 0) > 0 ||
                  (rateItem?.fertiliser_amount ?? 0) > 0;

                const hasBiologicalValues = Array.isArray(
                  rateItem?.bioTreatement
                )
                  ? rateItem.bioTreatement.some((treatment: any) => {
                      const selectedTreatments =
                        treatment?.selected_treatment ?? [];
                      return (
                        Boolean(treatment?.treated) ||
                        selectedTreatments.length > 0 ||
                        Boolean(treatment?.tank_application_volume)
                      );
                    })
                  : false;

                return (
                  hasSeedValues || hasFertiliserValues || hasBiologicalValues
                );
              })
            );

          if (hasLocalRatesData) return prev;

          return {
            ...prev,
            standard_seed_rate: sfp.standard_seed_rate,
            standard_product: sfp.standard_product,
            standard_seed_product_id: sfp.standard_seed_product_id,
            standard_fertilisers: (sfp.standard_fertilisers ?? []).map(
              (fertiliser: any, index: number) => ({
                ...fertiliser,
                id:
                  fertiliser?.id ??
                  fertiliser?.product_id ??
                  [
                    String(
                      fertiliser?.fertiliser ?? fertiliser?.name ?? "fertiliser"
                    ),
                    String(index),
                  ].join("-"),
                nutrients: Array.isArray(fertiliser?.nutrients)
                  ? fertiliser.nutrients
                  : [],
              })
            ),
            standard_biological_application_settings:
              sfp.standard_biological_application_settings ?? [],
            standard_tank_mix_rates: sfp.standard_tank_mix_rates ?? [],
          };
        });
      }
    }
  }, [trialData, form, setRatesDosage, setTrialInfo]);

  const handleToggleMapFullscreen = (): any => {
    setIsMapFullscreen((prevState) => !prevState);
  };

  const fetchProtocolData = async (): Promise<void> => {
    if (trialInfo?.protocol_id) {
      try {
        const response = await AxiosInstance.get(
          `protocol/${String(trialInfo.protocol_id)}`,
          { headers: extraHeaders }
        );
        setTrialProtocolData(response.data);
      } catch (error) {
        console.error("error fetching trial data", error);
      }
    }
  };

  useEffect(() => {
    const fetchDataAndHandleError = async (): Promise<void> => {
      try {
        await fetchProtocolData();
      } catch (error) {
        console.error(formatTranslation(t("common.error.fetch")), error);
      }
    };

    fetchDataAndHandleError().catch((error) => {
      console.error(formatTranslation(t("common.error.fetch")), error);
      return error;
    });

    if (typeof trialInfo?.completed_step === "number") {
      if (
        (trialInfo.completed_step === 2 || trialInfo.completed_step === 3) &&
        !didSetStep.current
      ) {
        setIsLoadingTrialPlotData(true);
        if (trialInfo.completed_step === 3) {
          setCurrent(1);
          setTimeout(() => {
            setCurrent(2);
            setTimeout(() => {
              setCurrent(3);
              setIsLoadingTrialPlotData(false);
            }, 2000);
          }, 500);
        } else {
          setCurrent(1);
          setTimeout(() => {
            setCurrent(trialInfo.completed_step);
            setIsLoadingTrialPlotData(false);
          }, 500);
        }
        didSetStep.current = true;
      } else if (
        trialInfo.completed_step !== 2 &&
        trialInfo.completed_step !== 3
      ) {
        setCurrent(trialInfo.completed_step);
        didSetStep.current = false;
      }
    }
  }, [trialInfo, setCurrent]);

  const newTrialStyle: React.CSSProperties =
    current === 3
      ? {
          height: "calc(100vh - 200px)",
        }
      : {};

  const showModal = (): void => {
    setOpen(true);
  };

  const hideAgLoader = (): void => {
    setAgLoaderOpen(false);
    setErrorMessage("");
    setAgOperationids([]);
  };

  // When the loader modal opens, refetch the latest trial data. Once the fetch
  // settles, hide the loader and hand off to the export flow via showModal().
  useEffect(() => {
    if (!agLoaderOpen) return;
    // Start each open with a clean slate so a previous run's error is not
    // shown while the refetch is in flight.
    setErrorMessage("");
    let cancelled = false;
    void (async () => {
      // Start from the latest trial we already have; the refetch result below
      // supersedes it. `trialInfo` from the closure is stale for this run, so
      // the ids/status must be read from the freshly fetched payload.
      let latestTrial = trialInfo;
      try {
        const { data } = await refetchTrial();
        latestTrial = data ?? trialInfo;
      } catch (error) {
        console.error(formatTranslation(t("common.error.fetch")), error);
      } finally {
        if (!cancelled) {
          const fetchedAgOperationIds = Object.values(
            latestTrial?.ag_operations ?? {}
          )
            .flat()
            .filter((value): value is string => typeof value === "string");
          setAgOperationids(fetchedAgOperationIds);

          if ((latestTrial?.ag_operation_status ?? "") !== "") {
            setErrorMessage(
              formatTranslation(t("trials.agOpCreationfailedError"))
            );
          } else if (
            fetchedAgOperationIds.length === 0 ||
            fetchedAgOperationIds.length !==
              trialProtocolData?.protocol_type.length
          ) {
            setErrorMessage(formatTranslation(t("trials.missingAgOpIdError")));
          } else {
            // Data is available — clear any previous error and hand off to the
            // export flow.
            setErrorMessage("");
            setAgLoaderOpen(false);
            showModal();
          }
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [agLoaderOpen]);

  /**
   * local fix: handleViewTrialReport is async so it can await onFinish before
   * opening the drawer, ensuring the report API reads the latest summary data.
   * reportDrawerKey is incremented to force a full remount of TrialReportDrawer,
   * clearing any stale state from a previous report session.
   *
   * FIX (Copilot suggestion): onFinish catches errors internally and returns void,
   * so we wrap it in a try/catch. If it throws unexpectedly, we abort opening the
   * drawer to prevent the report being generated from stale/unsaved data.
   */
  const handleViewTrialReport = async (trialId: string): Promise<void> => {
    if (!trialId) {
      console.error("Cannot generate report: trial ID is undefined");
      return;
    }

    // Persist summary step data (including recalculated totals) before
    // generating/opening the report drawer, so report API reads fresh values.
    if (current === 3) {
      try {
        void trackTrialsEvents({
          event: TrialAmplitudeEvent.SAVE_AND_SHARE_REPORT,
          originAction: "Save and Share Report",
        });
        await onFinish(form, true, { skipNavigateOnSummary: true });
      } catch (saveError) {
        // onFinish failed unexpectedly — abort to avoid opening the drawer
        // with stale/unsaved summary data.
        console.error(
          "Failed to persist summary data before generating report. Aborting report drawer open.",
          saveError
        );
        return;
      }
    }

    setReportDrawerKey((prevKey) => prevKey + 1);
    setDrawerVisible(true);
  };

  const generateReport = async (
    trialId: string[],
    fieldIds: string[]
  ): Promise<{ uuid: string; reportId: string }> => {
    try {
      const response = await AxiosInstance.post("/online_report/generate", {
        trial_ids: trialId,
        field_ids: fieldIds,
      });
      const uuid = response.data?.UUID || response.data?.uuid;
      const reportId = response.data?.report_id;
      if (!uuid || !reportId) {
        throw new Error("No report UUID or reportId received in response");
      }
      return { uuid, reportId };
    } catch (error) {
      console.error("Report generation failed:", error);
      throw error;
    }
  };

  const hideDrawer = (): void => {
    void trackTrialsEvents({
      event: TrialAmplitudeEvent.CLOSE_TRIAL_REPORT_DRAWER,
      originAction: "Close Drawer",
    });
    setDrawerVisible(false);
  };

  const handleOk = (data: monitorModaldata, fielFormdData: any): void => {
    if (
      data.selectedmonitorBrand === "isoxml" ||
      data.selectedmonitorModel === "isoxml"
    ) {
      downloadISOXMLOrder(fielFormdData, trialInfo?.id).catch((error: any) => {
        console.error(formatTranslation(t("common.error.fetch")), error);
      });
    } else {
      downloadmonitorModalForm(data).catch((error: any) => {
        console.error(formatTranslation(t("common.error.fetch")), error);
      });
    }
    setOpen(false);
  };

  const handleCancel = (): void => {
    setOpen(false);
  };

  const handleMenuClick = (e: any): void => {
    void trackTrialsEvents({
      event: TrialAmplitudeEvent.TRIAL_STEPS,
      originAction: e.key,
    });
    if (e.key.toLowerCase() === "saveandsharereport") {
      if (trialInfo?.id) {
        // local fix: void the returned Promise to satisfy no-floating-promises
        void handleViewTrialReport(trialInfo.id);
      } else {
        console.error("Cannot view trial report: trial ID is undefined");
      }
    } else if (e.key.toLowerCase() === "openmodel") {
      void trackTrialsEvents({
        event: TrialAmplitudeEvent.SAVE_AND_EXPORT_WORK_ORDER,
        originAction: "Save and Export Work Order",
      });
      if (enableNewWorkorderExport) {
        // Open the loader modal; the effect below refetches the latest trial
        // data and then hands off to the export flow via showModal().
        setAgLoaderOpen(true);
      } else {
        showModal();
      }
    }
  };

  useEffect(() => {
    return () => {
      resetStates();
    };
  }, []);

  const handleCancelClick = useCallback((): void => {
    if (current === 1 || current === 2) {
      prev();
    } else if (current > 0) {
      // FIX: void the Promise returned by onFinish to satisfy
      // no-floating-promises — onClick cannot be async here.
      void onFinish(form, true);
      prev();
    } else {
      setOpenBackModal(true);
    }
  }, [current, form, onFinish, prev]);

  const [isSavingRatesSubStep, setIsSavingRatesSubStep] = useState(false);

  const handleNextClick = useCallback((): void => {
    if (current === 2 && nextRatesTrialType) {
      setIsSavingRatesSubStep(true);
      // Persist the current sub-tab's data before switching tabs, sending
      // only the sections filled in so far so the API doesn't reject an
      // empty, not-yet-visited section. If the save fails, stay on the
      // current sub-tab instead of moving to nextRatesTrialType.
      void saveRatesDosageSubStep(nextRatesTrialType)
        .then((saved) => {
          if (saved) {
            setIsFertilizerTabActive(nextRatesTrialType === "nutrient");
            setActiveRatesTab(nextRatesTrialType);
          }
        })
        .finally(() => {
          setIsSavingRatesSubStep(false);
        });
      return;
    }

    form.submit();
  }, [
    current,
    nextRatesTrialType,
    form,
    saveRatesDosageSubStep,
    setIsFertilizerTabActive,
    setActiveRatesTab,
  ]);

  const handleFormFinish = useCallback(
    (...args: Parameters<typeof onFinish>): void => {
      // On the last rates & dosage sub-tab, nextRatesTrialType is undefined so
      // handleNextClick falls through to form.submit() instead of showing the
      // loader itself. Show it here for that case so the fertiliser/nutrient
      // tab's final save also has a loading indicator.
      const isFinalRatesSubStepSave = current === 2 && !nextRatesTrialType;
      if (isFinalRatesSubStepSave) {
        setIsSavingRatesSubStep(true);
      }
      // FIX: void the Promise returned by onFinish to satisfy
      // no-floating-promises — this is a sync event handler
      // so we cannot await here.
      void onFinish(...args).finally(() => {
        if (isFinalRatesSubStepSave) {
          setIsSavingRatesSubStep(false);
        }
      });
      void trackTrialsEvents({
        event: TrialAmplitudeEvent.TRIAL_STEPS,
        originAction: steps[current + 1]?.title,
      });
    },
    [onFinish, trackTrialsEvents, steps, current, nextRatesTrialType]
  );

  return (
    <div className="newTrail-page">
      {(isLoading ||
        isLoadingTrialData ||
        isLoadingTrialPlotData ||
        isSavingRatesSubStep) && <Loader mask message="" />}
      <BackConfirmationModal
        open={openBackModal}
        onOk={handleOkBackModal}
        onCancel={handleCancelModal}
      />
      <ToastContainer />
      <div className="newTrail-header">
        <TrialHeader
          isEditing={!!id}
          onBackClick={showBackModal}
          current={current}
          items={items}
        />
        <div
          className={`newTrail-data ${isMapFullscreen ? "map-open" : ""}`}
          style={newTrialStyle}
        >
          <div
            className={`left-sec ${current === 4 ? "hide-map" : ""} ${
              isMapFullscreen ? "map-open" : ""
            }`}
          >
            <div
              className={`inner-sec ${current === 2 ? "remove-padding" : ""}`}
            >
              <div>
                <TrialForm
                  form={form}
                  current={current}
                  trialData={trialData}
                  onFormFinish={handleFormFinish}
                  cancelLabel={getCancelButtonLabel(current, t)}
                  nextLabel={primaryNextLabel}
                  isStepValid={effectiveCheckValueForStep}
                  isNextDisabled={
                    !effectiveCheckValueForStep ||
                    isLoadingTrialData ||
                    isLoadingTrialPlotData ||
                    isSavingRatesSubStep
                  }
                  onCancelClick={handleCancelClick}
                  onNextClick={handleNextClick}
                />
              </div>
            </div>
          </div>
          {current !== 4 && (
            <div className={`right-sec ${isMapFullscreen ? "map-open" : ""}`}>
              <Button className="btn-map" onClick={handleToggleMapFullscreen}>
                {isMapFullscreen ? <ArrowInBtn /> : <ArrowOutBtn />}
              </Button>
              <TrialMapComponent
                current={current}
                isMapFullscreen={isMapFullscreen}
                isFertilizerTabActive={isFertilizerTabActive}
              />
            </div>
          )}
        </div>
        {current === 3 ? (
          <SummaryFooter
            current={current}
            goToSummary={goToSummary}
            prev={prev}
            form={form}
            onFinish={onFinish}
            steps={steps}
            trackTrialsEvents={trackTrialsEvents}
            menuItems={menuItems}
            handleMenuClick={handleMenuClick}
            trialInfo={trialInfo}
            handleViewTrialReport={handleViewTrialReport}
            enableNewWorkorderExport={enableNewWorkorderExport}
            open={open}
            handleCancel={handleCancel}
            handleOk={handleOk}
            reportDrawerKey={reportDrawerKey}
            drawerVisible={drawerVisible}
            hideDrawer={hideDrawer}
            generateReport={generateReport}
            agLoaderOpen={agLoaderOpen}
            hideAgLoader={hideAgLoader}
            agOperationIds={agOperationIds}
            errorMessage={errorMessage}
          />
        ) : null}
      </div>
    </div>
  );
};

export default React.memo(NewTrial);
